import type { ContentBlock } from "@diality/contracts";
import { Callout, HeroMediaCentered, PageRenderer } from "@diality/ui";
import { useLoaderData } from "react-router";
import type { MarketingPageLoaderData } from "../../marketing/page-loader";

/**
 * The passage the site shows when no CMS page is published at `/`.
 *
 * Block-level, matching the schema and `chaptersFromBlock`: three chapters of
 * scalars, three rich-text descriptions, and nine flat picture slots. Chapter N
 * owns slots 3N-2 (its feature picture) and 3N-1 / 3N (its two details), and the
 * assignments below preserve exactly which photograph each collage tile showed
 * under the previous repeated-item shape.
 *
 * The `cropId`s are absolute URLs rather than `{w}x{h}`, which is the documented
 * escape hatch for a composition written in code: `resolveMarketingMedia` passes
 * anything that is not a stored crop id straight through.
 */
const HOME_CHAPTERS: ContentBlock = {
  id: "home-static-chapters",
  versionId: "home-static",
  pageId: "home",
  locale: "en",
  componentId: "Chapters",
  position: 1,
  scalars: {
    sectionOneEyebrow: "See how it started",
    sectionOneTitle: "Why we exist",
    sectionOneCtaLabel: "Our Story",
    sectionOneCtaHref: "/our-story",
    sectionTwoEyebrow: "Take a closer look",
    sectionTwoTitle: "Built for your program",
    sectionTwoCtaLabel: "Explore our system",
    sectionTwoCtaHref: "/product",
    sectionThreeEyebrow: "See where we're headed",
    sectionThreeTitle: "Where we're headed",
    // This chapter has never offered one, and a call to action is indivisible:
    // a label with no link is half of one, so both stay empty.
    sectionThreeCtaLabel: "",
    sectionThreeCtaHref: "",
  },
  documents: {
    sectionOneDescription:
      {
        type: "doc",
        content: [
          { type: "paragraph", content: [{ type: "text", text: "Hemodialysis hasn't changed much in decades. We're rethinking it from the ground up — the engineering, the workflow, and the settings where care happens." }] },
        ],
      },
    sectionTwoDescription:
      {
        type: "doc",
        content: [
          { type: "paragraph", content: [{ type: "text", text: "Our system is designed to give clinicians one easy-to-learn system that fits a broad range of hemodialysis prescriptions — in the settings where dialysis lives today." }] },
        ],
      },
    sectionThreeDescription:
      {
        type: "doc",
        content: [
          { type: "paragraph", content: [{ type: "text", text: "One platform, built for one of medicine's most common recurring treatments. Follow the milestones — the technology, the team, and the path toward FDA clearance." }] },
        ],
      },
  },
  media: {
    // Chapter 1 — feature, then its two details.
    mediaOne: { cropId: "https://picsum.photos/seed/diality-story-team/1200/900" },
    mediaTwo: { cropId: "https://picsum.photos/seed/diality-story-office/900/675" },
    mediaThree: { cropId: "https://picsum.photos/seed/diality-story-lab/750/560" },
    // Chapter 2. `diality-program-part` is not carried over: it fed the fourth
    // tile the second composition used to declare, which the schema's three
    // pictures per chapter dropped.
    mediaFour: { cropId: "https://picsum.photos/seed/diality-program-system/900/675" },
    mediaFive: { cropId: "https://picsum.photos/seed/diality-program-design/1200/900" },
    mediaSix: { cropId: "https://picsum.photos/seed/diality-program-engineer/1200/900" },
    // Chapter 3.
    mediaSeven: {
      cropId: "https://picsum.photos/seed/diality-headed-conversation/900/675",
    },
    mediaEight: { cropId: "https://picsum.photos/seed/diality-headed-parts/1200/900" },
    mediaNine: {
      cropId: "https://picsum.photos/seed/diality-headed-development/750/560",
    },
  },
};

const HOME_PROMO: ContentBlock = {
  id: "home-static-promo",
  versionId: "home-static",
  pageId: "home",
  locale: "en",
  componentId: "Promo",
  position: 2,
  scalars: {
    eyebrow: "Patients & Education",
    title: "Dialysis, in plain language",
    ctaLabel: "Visit Patients & Education",
    ctaHref: "/patients",
  },
  media: {},
  documents: {
    description: {
      type: "doc",
      content: [
        {
          type: "paragraph",
          content: [
            {
              type: "text",
              text: "Resources for patients, families, and caregivers — what hemodialysis involves and what Moda-flx is designed to mean for daily care.",
            },
          ],
        },
      ],
    },
  },
};

/** The homepage as composed in code, when no CMS page is published at /. */
function staticHomeBlocks(): ContentBlock[] {
  return [HOME_CHAPTERS, HOME_PROMO];
}

function pageContentBlocks(blocks: ContentBlock[]): ContentBlock[] {
  return blocks.filter(
    (block) =>
      // The homepage introduction is now code-owned. Keep later
      // Hero blocks (such as the careers callout), but hide the legacy lead.
      !(block.componentId === "Hero" && block.position === 0) &&
      block.componentId !== "NewsList" &&
      block.scalars.emailCapture !== "true",
  );
}

export function HomePage() {
  const data = useLoaderData() as MarketingPageLoaderData | undefined;

  const hero = (
    <HeroMediaCentered
      videoSrc="/marketing/home-hero.mp4"
      posterSrc="/marketing/home-hero.jpg"
    />
  );
  const callout = <Callout>We build around the realities of care.</Callout>;

  // A published CMS page owns the whole homepage, opening included. The
  // code-owned hero and callout are deliberately NOT prepended here: an Editor
  // who placed a HeroMediaCentered and a Callout on the page would otherwise see
  // each of them twice.
  if (data?.kind === "cms" && data.blocks.length > 0) {
    return <PageRenderer blocks={pageContentBlocks(data.blocks)} />;
  }

  // Nothing published at `/` — the composed-in-code homepage. The hero and the
  // callout belong to this branch and only this one.
  return (
    <>
      {hero}
      {callout}
      <PageRenderer blocks={staticHomeBlocks()} />
    </>
  );
}
